import { createReadStream } from 'node:fs'
import {
  access,
  mkdtemp,
  mkdir,
  open,
  readdir,
  readFile,
  rm,
  stat,
} from 'node:fs/promises'
import { tmpdir } from 'node:os'
import { basename, dirname, join, posix, resolve } from 'node:path'
import { create as createTar, extract as extractTar } from 'tar'

const ARCHIVE_VERSION = 1
const LIST_PAGE_SIZE = 1000
const MAX_FILENAME_BYTES = 240

const STREAM_NEXT_OFFSET_HEADER = `Stream-Next-Offset`
const STREAM_UP_TO_DATE_HEADER = `Stream-Up-To-Date`
const STREAM_TTL_HEADER = `Stream-TTL`
const STREAM_EXPIRES_AT_HEADER = `Stream-Expires-At`
const STREAM_TOTAL_SIZE_HEADER = `Stream-Total-Size`
const STREAM_WRITE_UNITS_HEADER = `Stream-Write-Units`
const STREAM_CLOSED_HEADER = `Stream-Closed`
const STREAM_METADATA_HEADER = `Electric-Stream-Metadata`

interface ListedStream {
  id: string
  path: string
  contentType: string
  expiresAt: string | null
  metadata: Record<string, unknown>
  createdAt: string
}

interface StreamListPage {
  streams: ListedStream[]
  hasMore: boolean
  nextCursor: string | null
}

export interface StreamArchiveMetadata extends ListedStream {
  archiveVersion: 1
  exportedAt: string
  closed: boolean
  tailOffset: string | null
  ttlSeconds: string | null
  totalSize: string | null
  writeUnits: string | null
  archivedSize: number
}

export interface ExportConfig {
  serviceUrl: string
  readToken: string
  outputDirectory: string
}

export interface ImportConfig {
  archive: string
  serverUrl: string
  headers: Record<string, string>
}

export interface ExportResult {
  directory: string
  archive: string
  streamCount: number
}

export interface ImportResult {
  streamCount: number
}

type Logger = (message: string) => void

export function defaultExportDirectory(now: Date = new Date()): string {
  const timestamp = now.toISOString().replace(/[:.]/g, `-`)
  return resolve(`durable-streams-${timestamp}`)
}

export function parseHeader(value: string): [string, string] {
  const separator = value.indexOf(`:`)
  if (separator <= 0) {
    throw new Error(`Invalid --header value "${value}"; expected "Name: value"`)
  }

  const name = value.slice(0, separator).trim()
  const headerValue = value.slice(separator + 1).trim()
  if (!name) {
    throw new Error(`Invalid --header value "${value}"; expected "Name: value"`)
  }

  try {
    new Headers({ [name]: headerValue })
  } catch {
    throw new Error(`Invalid HTTP header "${name}"`)
  }

  return [name, headerValue]
}

export function normalizeBaseUrl(value: string): string {
  let url: URL
  try {
    url = new URL(value)
  } catch {
    throw new Error(`Invalid URL: ${value}`)
  }

  if (url.protocol !== `http:` && url.protocol !== `https:`) {
    throw new Error(`URL must use http or https: ${value}`)
  }
  if (url.search || url.hash) {
    throw new Error(`URL must not contain a query string or fragment: ${value}`)
  }

  return url.href.replace(/\/$/, ``)
}

export function encodeStreamFilename(streamPath: string): string {
  if (!streamPath) throw new Error(`Stream path must not be empty`)
  const encoded = encodeURIComponent(streamPath)
  if (Buffer.byteLength(`${encoded}.json`) > MAX_FILENAME_BYTES) {
    throw new Error(
      `URL-encoded stream path exceeds ${MAX_FILENAME_BYTES} filename bytes: ${streamPath}`
    )
  }
  return encoded
}

export function streamUrl(baseUrl: string, streamPath: string): string {
  const encodedPath = streamPath
    .split(`/`)
    .map((segment) => encodeURIComponent(segment))
    .join(`/`)
  return `${normalizeBaseUrl(baseUrl)}/${encodedPath}`
}

function controlListUrl(serviceUrl: string, cursor?: string): string {
  const url = new URL(`${normalizeBaseUrl(serviceUrl)}/__ds/streams`)
  url.searchParams.set(`limit`, String(LIST_PAGE_SIZE))
  if (cursor) url.searchParams.set(`cursor`, cursor)
  return url.href
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === `object` && value !== null && !Array.isArray(value)
}

function isIsoDate(value: string): boolean {
  return !Number.isNaN(new Date(value).getTime())
}

function parseListedStream(value: unknown): ListedStream {
  if (!isRecord(value))
    throw new Error(`Stream list contains a non-object item`)
  const { id, path, contentType, expiresAt, metadata, createdAt } = value
  if (
    typeof id !== `string` ||
    typeof path !== `string` ||
    path.length === 0 ||
    typeof contentType !== `string` ||
    contentType.length === 0 ||
    !isRecord(metadata) ||
    typeof createdAt !== `string` ||
    !isIsoDate(createdAt) ||
    (expiresAt !== null &&
      (typeof expiresAt !== `string` || !isIsoDate(expiresAt)))
  ) {
    throw new Error(`Stream list contains invalid metadata`)
  }
  return {
    id,
    path,
    contentType,
    expiresAt: expiresAt as string | null,
    metadata,
    createdAt,
  }
}

function parseListPage(value: unknown): StreamListPage {
  if (!isRecord(value) || !Array.isArray(value.streams)) {
    throw new Error(`Stream list response has an invalid shape`)
  }
  if (
    typeof value.hasMore !== `boolean` ||
    (value.nextCursor !== null && typeof value.nextCursor !== `string`)
  ) {
    throw new Error(`Stream list response has invalid pagination metadata`)
  }
  const hasMore = value.hasMore as boolean
  const nextCursor = value.nextCursor as string | null
  if (hasMore !== (nextCursor !== null)) {
    throw new Error(`Stream list response has inconsistent pagination metadata`)
  }
  return {
    streams: value.streams.map(parseListedStream),
    hasMore,
    nextCursor,
  }
}

async function responseError(response: Response, operation: string) {
  const body = (await response.text()).slice(0, 2000).trim()
  const detail = body ? `: ${body}` : ``
  return new Error(`${operation} failed with HTTP ${response.status}${detail}`)
}

async function listAllStreams(
  serviceUrl: string,
  readToken: string,
  fetchClient: typeof fetch
): Promise<ListedStream[]> {
  const streams: ListedStream[] = []
  const seenCursors = new Set<string>()
  let cursor: string | undefined

  while (true) {
    const response = await fetchClient(controlListUrl(serviceUrl, cursor), {
      headers: { Authorization: `Bearer ${readToken}` },
    })
    if (!response.ok) throw await responseError(response, `Listing streams`)
    const page = parseListPage(await response.json())
    streams.push(...page.streams)

    if (!page.nextCursor) return streams
    if (seenCursors.has(page.nextCursor)) {
      throw new Error(`Stream list returned a repeated pagination cursor`)
    }
    seenCursors.add(page.nextCursor)
    cursor = page.nextCursor
  }
}

function normalizeContentType(value: string): string {
  return value.split(`;`, 1)[0]!.trim().toLowerCase()
}

function jsonArrayContents(value: string, streamPath: string): string {
  const trimmed = value.trim()
  if (!trimmed) return ``

  let parsed: unknown
  try {
    parsed = JSON.parse(trimmed)
  } catch {
    throw new Error(`Stream ${streamPath} returned invalid JSON`)
  }
  if (
    !Array.isArray(parsed) ||
    !trimmed.startsWith(`[`) ||
    !trimmed.endsWith(`]`)
  ) {
    throw new Error(`Stream ${streamPath} returned a non-array JSON page`)
  }
  return trimmed.slice(1, -1).trim()
}

async function writeResponseBody(
  file: Awaited<ReturnType<typeof open>>,
  response: Response
) {
  if (!response.body) return
  const reader = response.body.getReader()
  try {
    while (true) {
      const chunk = await reader.read()
      if (chunk.done) return
      await file.write(chunk.value)
    }
  } finally {
    reader.releaseLock()
  }
}

interface ReadStreamResult {
  closed: boolean
  tailOffset: string | null
  ttlSeconds: string | null
  expiresAt: string | null
  totalSize: string | null
  writeUnits: string | null
  archivedSize: number
  contentType: string
}

async function exportStreamData(args: {
  url: string
  readToken: string
  outputFile: string
  listedContentType: string
  streamPath: string
  fetchClient: typeof fetch
}): Promise<ReadStreamResult> {
  const authHeaders = { Authorization: `Bearer ${args.readToken}` }
  const head = await args.fetchClient(args.url, {
    method: `HEAD`,
    headers: authHeaders,
  })
  if (!head.ok)
    throw await responseError(head, `Reading metadata for ${args.streamPath}`)

  const contentType = head.headers.get(`Content-Type`) ?? args.listedContentType
  const isJson = normalizeContentType(contentType) === `application/json`
  const file = await open(args.outputFile, `wx`)
  let offset = `-1`
  let wroteJsonItems = false
  let tailOffset = head.headers.get(STREAM_NEXT_OFFSET_HEADER)
  let closed = head.headers.get(STREAM_CLOSED_HEADER)?.toLowerCase() === `true`

  try {
    if (isJson) await file.write(`[`)

    while (true) {
      const url = new URL(args.url)
      url.searchParams.set(`offset`, offset)
      const response = await args.fetchClient(url, { headers: authHeaders })
      if (!response.ok) {
        throw await responseError(response, `Reading ${args.streamPath}`)
      }

      if (isJson) {
        const contents = jsonArrayContents(
          await response.text(),
          args.streamPath
        )
        if (contents) {
          if (wroteJsonItems) await file.write(`,`)
          await file.write(contents)
          wroteJsonItems = true
        }
      } else {
        await writeResponseBody(file, response)
      }

      const upToDate = response.headers.has(STREAM_UP_TO_DATE_HEADER)
      const nextOffset = response.headers.get(STREAM_NEXT_OFFSET_HEADER)
      tailOffset = nextOffset ?? tailOffset
      closed =
        closed ||
        response.headers.get(STREAM_CLOSED_HEADER)?.toLowerCase() === `true`
      if (upToDate) break
      if (!nextOffset || nextOffset === offset) {
        throw new Error(
          `Stream ${args.streamPath} did not provide a progressing next offset`
        )
      }
      offset = nextOffset
    }

    if (isJson) await file.write(`]`)
  } finally {
    await file.close()
  }

  return {
    closed,
    tailOffset,
    ttlSeconds: head.headers.get(STREAM_TTL_HEADER),
    expiresAt: head.headers.get(STREAM_EXPIRES_AT_HEADER),
    totalSize: head.headers.get(STREAM_TOTAL_SIZE_HEADER),
    writeUnits: head.headers.get(STREAM_WRITE_UNITS_HEADER),
    archivedSize: (await stat(args.outputFile)).size,
    contentType,
  }
}

async function ensureMissing(path: string) {
  try {
    await access(path)
  } catch (error) {
    if (error instanceof Error && `code` in error && error.code === `ENOENT`) {
      return
    }
    throw error
  }
  throw new Error(`Refusing to overwrite existing path: ${path}`)
}

export async function exportDurableStreams(
  config: ExportConfig,
  options: { fetch?: typeof fetch; log?: Logger; now?: Date } = {}
): Promise<ExportResult> {
  const fetchClient = options.fetch ?? globalThis.fetch
  const log = options.log ?? console.log
  const exportedAt = (options.now ?? new Date()).toISOString()
  const directory = resolve(config.outputDirectory)
  const archive = `${directory}.tar.gz`

  await ensureMissing(directory)
  await ensureMissing(archive)
  const streams = await listAllStreams(
    config.serviceUrl,
    config.readToken,
    fetchClient
  )
  await mkdir(dirname(directory), { recursive: true })
  await mkdir(directory)

  for (const [index, stream] of streams.entries()) {
    const encodedName = encodeStreamFilename(stream.path)
    const dataFile = join(directory, `${encodedName}.bin`)
    const metadataFile = join(directory, `${encodedName}.json`)
    log(`[${index + 1}/${streams.length}] Exporting ${stream.path}`)

    const readMetadata = await exportStreamData({
      url: streamUrl(config.serviceUrl, stream.path),
      readToken: config.readToken,
      outputFile: dataFile,
      listedContentType: stream.contentType,
      streamPath: stream.path,
      fetchClient,
    })
    const metadata: StreamArchiveMetadata = {
      archiveVersion: ARCHIVE_VERSION,
      id: stream.id,
      path: stream.path,
      contentType: readMetadata.contentType,
      expiresAt: readMetadata.expiresAt ?? stream.expiresAt,
      metadata: stream.metadata,
      createdAt: stream.createdAt,
      exportedAt,
      closed: readMetadata.closed,
      tailOffset: readMetadata.tailOffset,
      ttlSeconds: readMetadata.ttlSeconds,
      totalSize: readMetadata.totalSize,
      writeUnits: readMetadata.writeUnits,
      archivedSize: readMetadata.archivedSize,
    }
    await open(metadataFile, `wx`).then(async (file) => {
      try {
        await file.writeFile(`${JSON.stringify(metadata, null, 2)}\n`)
      } finally {
        await file.close()
      }
    })
  }

  await createTar(
    {
      cwd: dirname(directory),
      file: archive,
      gzip: true,
      portable: true,
    },
    [basename(directory)]
  )

  return { directory, archive, streamCount: streams.length }
}

function requiredString(value: Record<string, unknown>, key: string): string {
  const item = value[key]
  if (typeof item !== `string` || item.length === 0) {
    throw new Error(`Archive metadata field ${key} must be a non-empty string`)
  }
  return item
}

export function parseArchiveMetadata(value: unknown): StreamArchiveMetadata {
  if (!isRecord(value) || value.archiveVersion !== ARCHIVE_VERSION) {
    throw new Error(`Unsupported or missing archiveVersion`)
  }

  const expiresAt = value.expiresAt
  if (
    expiresAt !== null &&
    (typeof expiresAt !== `string` || !isIsoDate(expiresAt))
  ) {
    throw new Error(`Archive metadata expiresAt must be null or an ISO date`)
  }
  if (!isRecord(value.metadata)) {
    throw new Error(`Archive metadata field metadata must be an object`)
  }
  if (typeof value.closed !== `boolean`) {
    throw new Error(`Archive metadata field closed must be a boolean`)
  }
  if (
    typeof value.archivedSize !== `number` ||
    !Number.isSafeInteger(value.archivedSize) ||
    value.archivedSize < 0
  ) {
    throw new Error(
      `Archive metadata field archivedSize must be a non-negative integer`
    )
  }

  const nullableString = (key: string): string | null => {
    const item = value[key]
    if (item === null) return null
    if (typeof item !== `string`) {
      throw new Error(`Archive metadata field ${key} must be a string or null`)
    }
    return item
  }

  const createdAt = requiredString(value, `createdAt`)
  const exportedAt = requiredString(value, `exportedAt`)
  if (!isIsoDate(createdAt) || !isIsoDate(exportedAt)) {
    throw new Error(`Archive creation timestamps must be ISO dates`)
  }

  return {
    archiveVersion: ARCHIVE_VERSION,
    id: requiredString(value, `id`),
    path: requiredString(value, `path`),
    contentType: requiredString(value, `contentType`),
    expiresAt: expiresAt as string | null,
    metadata: value.metadata,
    createdAt,
    exportedAt,
    closed: value.closed,
    tailOffset: nullableString(`tailOffset`),
    ttlSeconds: nullableString(`ttlSeconds`),
    totalSize: nullableString(`totalSize`),
    writeUnits: nullableString(`writeUnits`),
    archivedSize: value.archivedSize,
  }
}

function safeArchiveEntry(entryPath: string, type: string): boolean {
  const normalized = posix.normalize(entryPath)
  if (
    posix.isAbsolute(entryPath) ||
    normalized === `..` ||
    normalized.startsWith(`../`) ||
    normalized.includes(`/../`)
  ) {
    throw new Error(`Archive contains an unsafe path: ${entryPath}`)
  }
  if (![`File`, `OldFile`, `Directory`].includes(type)) {
    throw new Error(
      `Archive contains unsupported entry type ${type}: ${entryPath}`
    )
  }
  return true
}

async function walkFiles(directory: string): Promise<string[]> {
  const files: string[] = []
  for (const entry of await readdir(directory, { withFileTypes: true })) {
    const path = join(directory, entry.name)
    if (entry.isDirectory()) {
      files.push(...(await walkFiles(path)))
    } else if (entry.isFile()) {
      files.push(path)
    } else {
      throw new Error(
        `Archive contains an unsupported filesystem entry: ${path}`
      )
    }
  }
  return files
}

interface ImportEntry {
  metadata: StreamArchiveMetadata
  dataFile: string
}

async function readImportEntries(directory: string): Promise<ImportEntry[]> {
  const files = await walkFiles(directory)
  const fileSet = new Set(files)
  const metadataFiles = files.filter((file) => file.endsWith(`.json`)).sort()
  const dataFiles = files.filter((file) => file.endsWith(`.bin`))
  const unexpected = files.filter(
    (file) => !file.endsWith(`.json`) && !file.endsWith(`.bin`)
  )
  if (unexpected.length > 0) {
    throw new Error(`Archive contains an unexpected file: ${unexpected[0]}`)
  }

  const entries: ImportEntry[] = []
  const streamPaths = new Set<string>()
  for (const metadataFile of metadataFiles) {
    const stem = metadataFile.slice(0, -`.json`.length)
    const dataFile = `${stem}.bin`
    if (!fileSet.has(dataFile)) {
      throw new Error(`Missing data file for ${metadataFile}`)
    }

    const metadata = parseArchiveMetadata(
      JSON.parse(await readFile(metadataFile, `utf8`)) as unknown
    )
    const encodedName = basename(stem)
    let decodedName: string
    try {
      decodedName = decodeURIComponent(encodedName)
    } catch {
      throw new Error(`Invalid URL-encoded stream filename: ${encodedName}`)
    }
    if (decodedName !== metadata.path) {
      throw new Error(
        `Stream filename ${encodedName} does not match metadata path ${metadata.path}`
      )
    }
    if (streamPaths.has(metadata.path)) {
      throw new Error(
        `Archive contains duplicate stream path: ${metadata.path}`
      )
    }
    streamPaths.add(metadata.path)

    const dataSize = (await stat(dataFile)).size
    if (dataSize !== metadata.archivedSize) {
      throw new Error(
        `Data size for ${metadata.path} is ${dataSize}, expected ${metadata.archivedSize}`
      )
    }
    entries.push({ metadata, dataFile })
  }

  if (dataFiles.length !== entries.length) {
    throw new Error(`Archive contains a data file without matching metadata`)
  }
  return entries
}

function importHeaders(
  customHeaders: Record<string, string>,
  metadata: StreamArchiveMetadata
): Headers {
  const headers = new Headers(customHeaders)
  headers.set(`Content-Type`, metadata.contentType)
  headers.set(STREAM_METADATA_HEADER, JSON.stringify(metadata.metadata))
  if (metadata.expiresAt) {
    headers.set(STREAM_EXPIRES_AT_HEADER, metadata.expiresAt)
  } else if (metadata.ttlSeconds !== null) {
    headers.set(STREAM_TTL_HEADER, metadata.ttlSeconds)
  }
  if (metadata.closed) headers.set(STREAM_CLOSED_HEADER, `true`)
  return headers
}

type StreamingRequestInit = RequestInit & { duplex: `half` }

async function importEntry(
  config: ImportConfig,
  entry: ImportEntry,
  fetchClient: typeof fetch
) {
  const url = streamUrl(config.serverUrl, entry.metadata.path)
  const target = await fetchClient(url, {
    method: `HEAD`,
    headers: config.headers,
  })
  if (target.ok) {
    throw new Error(`Target stream already exists: ${entry.metadata.path}`)
  }
  if (target.status !== 404) {
    throw await responseError(target, `Checking target ${entry.metadata.path}`)
  }

  const dataSize = (await stat(entry.dataFile)).size
  const request: StreamingRequestInit = {
    method: `PUT`,
    headers: importHeaders(config.headers, entry.metadata),
    duplex: `half`,
  }
  if (dataSize > 0) {
    request.body = createReadStream(
      entry.dataFile
    ) as unknown as RequestInit[`body`]
  }

  const response = await fetchClient(url, request)
  if (!response.ok) {
    throw await responseError(response, `Creating ${entry.metadata.path}`)
  }
}

export async function importDurableStreams(
  config: ImportConfig,
  options: { fetch?: typeof fetch; log?: Logger } = {}
): Promise<ImportResult> {
  const fetchClient = options.fetch ?? globalThis.fetch
  const log = options.log ?? console.log
  const temporaryDirectory = await mkdtemp(
    join(tmpdir(), `durable-stream-import-`)
  )

  try {
    await extractTar({
      cwd: temporaryDirectory,
      file: config.archive,
      strict: true,
      filter: (entryPath, entry) => {
        const type =
          `type` in entry
            ? entry.type
            : entry.isDirectory()
              ? `Directory`
              : entry.isFile()
                ? `File`
                : `Unknown`
        return safeArchiveEntry(entryPath, type)
      },
    })
    const entries = await readImportEntries(temporaryDirectory)

    for (const [index, entry] of entries.entries()) {
      log(`[${index + 1}/${entries.length}] Importing ${entry.metadata.path}`)
      await importEntry(config, entry, fetchClient)
    }

    return { streamCount: entries.length }
  } finally {
    await rm(temporaryDirectory, { recursive: true, force: true })
  }
}
